import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { DOMAINS, TASKS } from "@xinghai/shared";
import type { Domain, Task } from "@xinghai/shared";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  Tag,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { annotateApi, arbitrationApi, qualityApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { readPace, readQuickMode, writeQuickMode } from "./draft";
import { Illustration } from "../shared/Illustration";
import {
  DOMAIN_LABEL,
  TASK_LABEL_EXPERT,
  formatAverage,
  formatDuration,
  formatRemaining,
} from "./labels";
import type { AnnotateQueueRow } from "./types";

export function workHref(recordingId: string, quick = false): string {
  return `/a/work/${encodeURIComponent(recordingId)}${quick ? "?quick=1" : ""}`;
}

/**
 * docs/UX.md §2.1 — `/a` is a 待办收件箱, not a table.
 * Three cards (待标注 / 待复评 / 待仲裁), one primary action, a pace line, and
 * the old filters + queue table folded away under 「更多」.
 */
export function QueuePage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const userId = me?.user.id ?? "anon";
  const isAdjudicator = (me?.user.roles ?? []).some(
    (r) => r === "adjudicator" || r === "course_team",
  );

  const [domain, setDomain] = useState<Domain | "all">("all");
  const [task, setTask] = useState<Task | "all">("all");
  const [batchId, setBatchId] = useState("");
  // docs/ROADMAP.md B3 / docs/UX.md §2.2 — remembered per browser.
  const [quick, setQuick] = useState<boolean>(() => readQuickMode());

  const filters = useMemo(
    () => ({
      ...(batchId ? { batch_id: batchId } : {}),
      ...(task !== "all" ? { task } : {}),
      ...(domain !== "all" ? { domain } : {}),
    }),
    [batchId, task, domain],
  );

  const q = useQuery({
    queryKey: qk.annotateQueue(filters),
    queryFn: () => annotateApi.queue(filters),
  });
  const rereview = useQuery({
    queryKey: qk.annotateRereview,
    queryFn: () => annotateApi.rereview(),
    retry: false,
  });
  const arbitration = useQuery({
    queryKey: qk.arbitrationQueue,
    queryFn: () => arbitrationApi.queue(),
    enabled: isAdjudicator,
    retry: false,
  });
  const quality = useQuery({
    queryKey: qk.qualityAnnotators,
    queryFn: () => qualityApi.annotators(),
    retry: false,
  });

  const items = q.data?.items ?? [];
  const counts = q.data?.counts;
  const queued = counts?.queued ?? items.length;
  // B4 —— 「分配给我 n 条」排在最前；服务端也按同样的顺序排过队列。
  const assignedCount = counts?.assigned ?? items.filter((i) => i.assigned_to_me).length;
  const assignedNext = items.find((i) => i.assigned_to_me) ?? null;
  // B5 —— 周度 IAA 提示（默认只提示；`XZ_IAA_ENFORCE=on` 时队列真的会被扣住）
  const iaa = q.data?.iaa ?? null;
  const rereviewCount = rereview.data?.items.length ?? 0;
  const arbitrationCount = arbitration.data?.items.length ?? 0;
  const todayCount =
    quality.data?.annotators.find((a) => a.annotator_id === userId)?.today ?? counts?.today ?? 0;

  const pace = useMemo(() => readPace(userId), [userId]);
  const remainingMs = pace.avgMs === null ? null : pace.avgMs * queued;

  const next = items[0] ?? null;
  const continueHref = next
    ? workHref(next.recording_id, quick && next.task === "T2_diagnostic")
    : null;

  // Batch options come from whatever the queue currently holds.
  const batches = useMemo(() => {
    const seen = new Set<string>();
    for (const i of items) if (i.batch_id) seen.add(i.batch_id);
    if (batchId) seen.add(batchId);
    return [...seen].sort();
  }, [items, batchId]);

  const columns: ReadonlyArray<Column<AnnotateQueueRow>> = [
    {
      key: "recording_id",
      header: "录音编号",
      render: (r) => <span className="gz-type-data">{r.recording_id}</span>,
    },
    {
      key: "task",
      header: "任务",
      render: (r) => (
        <Tag tone="info">
          {TASK_LABEL_EXPERT[r.task]} · {DOMAIN_LABEL[r.domain]}
        </Tag>
      ),
    },
    {
      key: "material",
      header: "素材标题",
      render: (r) => r.material?.title ?? r.material?.text ?? "—",
    },
    {
      key: "duration",
      header: "时长",
      align: "end",
      hideOnMobile: true,
      render: (r) => formatDuration(r.duration_ms),
    },
    {
      key: "go",
      header: "操作",
      align: "end",
      render: (r) => (
        <Link
          className="gz-button gz-button--primary"
          to={workHref(r.recording_id, quick && r.task === "T2_diagnostic")}
        >
          开始标注
        </Link>
      ),
    },
  ];

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
        <Skeleton variant="rect" height={56} />
      </div>
    );
  }

  return (
    <div className="xz-container xz-stack xz-inbox">
      <h1 className="gz-type-heading">今天的待办</h1>

      {iaa?.hint && (
        <p className={iaa.withheld ? "gz-notice gz-notice--error" : "gz-notice"} role="status">
          <Icon id="ui-warning" size={20} />
          <span>
            {iaa.hint}
            {iaa.kappa !== null && (
              <>
                （本周 κ {iaa.kappa.toFixed(2)}，目标 ≥ {iaa.gate.toFixed(2)}）
              </>
            )}
            {iaa.withheld && "。在校准批达标之前，不会再派新的普通条目。"}
          </span>
        </p>
      )}

      <ul className="xz-inbox__cards">
        {assignedCount > 0 && (
          <li>
            <Card padding="md" className="xz-inbox__card">
              <p className="xz-inbox__label">分配给我</p>
              <p className="xz-inbox__count gz-type-score">{assignedCount}</p>
              {assignedNext ? (
                <Link
                  className="gz-button gz-button--primary xz-button--lg"
                  to={workHref(
                    assignedNext.recording_id,
                    quick && assignedNext.task === "T2_diagnostic",
                  )}
                >
                  开始
                </Link>
              ) : (
                <p className="gz-caption">分配给你的条目都做完了。</p>
              )}
            </Card>
          </li>
        )}
        <li>
          <Card padding="md" className="xz-inbox__card">
            <p className="xz-inbox__label">待标注</p>
            <p className="xz-inbox__count gz-type-score">{queued}</p>
            {continueHref ? (
              <Link className="gz-button gz-button--primary xz-button--lg" to={continueHref}>
                继续标注
              </Link>
            ) : (
              <>
                <Illustration name="empty-inbox" size="sm" />
                <p className="gz-caption">队列空了，休息一下。</p>
              </>
            )}
          </Card>
        </li>
        <li>
          <Card padding="md" className="xz-inbox__card">
            <p className="xz-inbox__label">待复评</p>
            <p className="xz-inbox__count gz-type-score">{rereviewCount}</p>
            <Link className="gz-button" to="/a/rereview">
              去复评
            </Link>
          </Card>
        </li>
        {isAdjudicator && (
          <li>
            <Card padding="md" className="xz-inbox__card">
              <p className="xz-inbox__label">待仲裁</p>
              <p className="xz-inbox__count gz-type-score">{arbitrationCount}</p>
              <Link className="gz-button" to="/j">
                去仲裁
              </Link>
            </Card>
          </li>
        )}
      </ul>

      <p className="xz-inbox__pace gz-caption" role="status">
        今天已完成 <b>{todayCount}</b> 条 · 平均每条 <b>{formatAverage(pace.avgMs)}</b> · 预计剩余{" "}
        <b>{formatRemaining(remainingMs)}</b>
      </p>

      <details className="xz-inbox__more">
        <summary>更多</summary>

        <div className="xz-queue__filters">
          <SegmentedControl<Domain | "all">
            size="sm"
            label="按域筛选"
            options={[
              { value: "all", label: "全部域" },
              ...DOMAINS.map((d) => ({ value: d, label: DOMAIN_LABEL[d] })),
            ]}
            value={domain}
            onChange={setDomain}
          />
          <Select
            aria-label="按任务筛选"
            value={task}
            onChange={(e) => setTask(e.target.value as Task | "all")}
          >
            <option value="all">全部任务</option>
            {TASKS.map((t) => (
              <option key={t} value={t}>
                {TASK_LABEL_EXPERT[t]}
              </option>
            ))}
          </Select>
          <Select
            aria-label="按批次筛选"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
          >
            <option value="">全部批次</option>
            {batches.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Select>
          <label className="xz-queue__quick">
            <input
              type="checkbox"
              checked={quick}
              onChange={(e) => {
                setQuick(e.target.checked);
                writeQuickMode(e.target.checked);
              }}
            />
            <span>快速模式</span>
            <small className="gz-help">
              仅单字任务：自动播放、默认「正确」、Enter 直接提交下一条。
            </small>
          </label>
          {(domain !== "all" || task !== "all" || batchId) && (
            <Button
              variant="ghost"
              onClick={() => {
                setDomain("all");
                setTask("all");
                setBatchId("");
              }}
            >
              清除筛选
            </Button>
          )}
        </div>

        {q.isError ? (
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} /> 队列没有加载出来，请刷新重试。
          </p>
        ) : (
          <Table<AnnotateQueueRow>
            caption="待标注录音"
            columns={columns}
            rows={items}
            rowKey={(r) => r.recording_id}
            onRowClick={(r) =>
              void navigate(workHref(r.recording_id, quick && r.task === "T2_diagnostic"))
            }
            empty={
              <EmptyState
                icon={<Icon id="ui-inbox" size={24} />}
                title="队列里暂时没有录音"
                description="新的录音通过质检后会自动派发到这里。换个筛选条件也可以看看。"
              />
            }
          />
        )}
      </details>
    </div>
  );
}
